import { ChangeEvent, useEffect, useState } from "react";
import { Accordion, AccordionHeader } from "react-bootstrap";
import Select from "react-select";
import { useSelector } from "react-redux";
import SpinnerButton from "../../../../components/SpinnerButton";
import ConditionalSpinner from "../../../../components/ConditionalSpinner";
import { capitaliseFirst } from "../../../../utilities/helpers";
import { Notifications } from "../../../../utilities/Notifications/Notifications";
import { User, UserGroup, PermissionAction, Permission } from "../../../../models/User";
import { ApplicationState } from "../../../../store";
import {
    useGetProjectQuery,
    useGetProjectPermissionsQuery,
    useGetUserGroupsQuery,
    useGetUsersQuery,
    useRemoveUserPermissionMutation,
    useRemoveGroupPermissionMutation,
    useAddUserPermissionMutation,
    useAddGroupPermissionMutation,
} from "../../../../store/api/kinesense";
import { PermissionCell } from "./AddProjectModal";
import { AccordionEventKey } from "react-bootstrap/esm/AccordionContext";
import { sleep } from "../../../../utilities/helpers";
import { Project } from "../../../../models/Project";
import { DateFormats } from "../../../../utilities/dates";
import dayjs from "dayjs";

const PERMISSION_ACTIONS = ["peek", "read", "update", "manage", "delete"] as const;
const DELAY_BETWEEN_CALLS = 100; // milliseconds

type PermissionMap = Map<string, PermissionAction[]>;

// Helper function to extract error message from RTK Query error or standard Error
const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === "object" && "data" in error) {
        const rtqError = error as { data?: { message?: string } };
        if (rtqError.data?.message) return rtqError.data.message;
    }
    if (error instanceof Error) return error.message;
    return "Unknown error";
};

// Check if permission actions have changed
const hasPermissionsChanged = (currentActions: PermissionAction[], originalActions: PermissionAction[]): boolean => {
    return (
        currentActions.length !== originalActions.length || !currentActions.every((a) => originalActions.includes(a))
    );
};

// Get original actions from permission map
const getOriginalActions = (id: string, originalPermsMap: Map<string, Permission>): PermissionAction[] => {
    return originalPermsMap.get(id)?.actions || [];
};

interface ProjectDetailsProps {
    project: Project;
}

interface PermissionsTableProps {
    items: { groupId?: string; username?: string; name?: string }[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    itemKey: (item: any) => string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    itemName: (item: any) => string;
    permissions: PermissionMap;
    onPermissionChange: (input: ChangeEvent<HTMLInputElement>, id: string, action: PermissionAction) => void;
}

function PermissionsTable({ items, itemKey, itemName, permissions, onPermissionChange }: PermissionsTableProps) {
    return (
        <table style={{ width: "100%" }}>
            <thead>
                <tr>
                    <th style={{ width: "30%" }}>Name</th>
                    {PERMISSION_ACTIONS.map((action) => (
                        <th key={action} style={{ textAlign: "center", padding: "0 0.5rem" }}>
                            {capitaliseFirst(action)}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {items.map((item) => {
                    const id = itemKey(item);
                    return (
                        <tr key={id}>
                            <td>{itemName(item)}</td>
                            {PERMISSION_ACTIONS.map((action) => (
                                <PermissionCell
                                    key={action}
                                    action={action}
                                    permissions={permissions}
                                    id={id}
                                    changed={(input) => onPermissionChange(input, id, action)}
                                />
                            ))}
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

export function ProjectDetails(props: ProjectDetailsProps) {
    const user = useSelector((state: ApplicationState) => state.user);
    const userCanViewOtherUsers =
        User.can(user.permissions.permissions, "read", "users") ||
        User.can(user.permissions.permissions, "peek", "users");

    const [permissionsAccordionKeys, setPermissionsAccordionKeys] = useState<string[]>([]);
    const isPermissionsOpen = permissionsAccordionKeys.length > 0;

    const { data: projectPermissions, isFetching: isLoadingPermissions } = useGetProjectPermissionsQuery(
        {
            projectId: props.project.projectId,
        },
        { skip: !isPermissionsOpen },
    );
    const { data: userGroups, isLoading: isLoadingGroups } = useGetUserGroupsQuery();
    const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery({}, { skip: !userCanViewOtherUsers });
    const [removeUserPermission] = useRemoveUserPermissionMutation();
    const [removeGroupPermission] = useRemoveGroupPermissionMutation();
    const [addUserPermission] = useAddUserPermissionMutation();
    const [addGroupPermission] = useAddGroupPermissionMutation();
    const [isSavingPermissions, setIsSavingPermissions] = useState(false);

    const [allowedGroups, setAllowedGroups] = useState<UserGroup[]>([]);
    const [allowedUsers, setAllowedUsers] = useState<User[]>([]);
    const [groupPermissions, setGroupPermissions] = useState<PermissionMap>(new Map());
    const [userPermissions, setUserPermissions] = useState<PermissionMap>(new Map());

    // Track original permissions for comparison
    const [originalGroupPermissions, setOriginalGroupPermissions] = useState<Map<string, Permission>>(new Map());
    const [originalUserPermissions, setOriginalUserPermissions] = useState<Map<string, Permission>>(new Map());

    useEffect(() => {
        if (projectPermissions && userGroups) {
            const groupPermissions = projectPermissions.filter(
                (perm) => perm.target.type === "group" && perm.subject === "projects",
            );
            const groupIds = groupPermissions.map((perm) => perm.target.targetId);
            const selectedGroups = userGroups.filter((group) => groupIds.includes(group.groupId));
            setAllowedGroups(selectedGroups);

            const mappedPermissions = new Map<string, PermissionAction[]>();
            const originalPermissions = new Map<string, Permission>();
            groupPermissions.forEach((perm) => {
                mappedPermissions.set(perm.target.targetId, perm.actions);
                originalPermissions.set(perm.target.targetId, perm);
            });
            setGroupPermissions(mappedPermissions);
            setOriginalGroupPermissions(originalPermissions);
        }
    }, [projectPermissions, userGroups]);

    useEffect(() => {
        if (projectPermissions && users) {
            const userPerms = projectPermissions.filter(
                (perm) => perm.target.type === "user" && perm.subject === "projects",
            );
            const usernames = userPerms.map((perm) => perm.target.targetId);
            const selectedUsers = users.filter((u) => usernames.includes(u.username));
            setAllowedUsers(selectedUsers);

            const mappedPermissions = new Map<string, PermissionAction[]>();
            const originalPermissions = new Map<string, Permission>();
            userPerms.forEach((perm) => {
                mappedPermissions.set(perm.target.targetId, perm.actions);
                originalPermissions.set(perm.target.targetId, perm);
            });
            setUserPermissions(mappedPermissions);
            setOriginalUserPermissions(originalPermissions);
        }
    }, [projectPermissions, users]);

    const handleAllowedGroupsChange = (selectedOptions: { value: string; label: string }[]) => {
        if (userGroups) {
            const selected = userGroups.filter((group) =>
                selectedOptions.some((option) => option.value === group.groupId),
            );

            setGroupPermissions(() => {
                const newMap = new Map<string, PermissionAction[]>();
                selected.forEach((group) => {
                    if (!newMap.has(group.groupId)) {
                        newMap.set(group.groupId, groupPermissions.get(group.groupId) || [...PERMISSION_ACTIONS]);
                    }
                });

                return newMap;
            });

            setAllowedGroups(selected);
        }
    };

    const handleAllowedUsersChange = (selectedOptions: { value: string; label: string }[]) => {
        if (users) {
            const selected = users.filter((user) => selectedOptions.some((option) => option.value === user.username));

            setUserPermissions(() => {
                const newMap = new Map<string, PermissionAction[]>();
                selected.forEach((user) => {
                    if (!newMap.has(user.username)) {
                        newMap.set(user.username, userPermissions.get(user.username) || [...PERMISSION_ACTIONS]);
                    }
                });

                return newMap;
            });

            setAllowedUsers(selected);
        }
    };

    const handlePermissionChange = (
        input: ChangeEvent<HTMLInputElement>,
        targetId: string,
        action: PermissionAction,
        stateSetter: (updater: (prev: PermissionMap) => PermissionMap) => void,
    ) => {
        const isChecked = input.target.checked;
        stateSetter((prev) => {
            const perms = [...(prev.get(targetId) || [])];

            if (isChecked) {
                perms.push(action);

                if (action == "read" && !perms.includes("peek")) {
                    perms.push("peek");
                }

                if (action == "update" || (action == "delete" && !perms.includes("read"))) {
                    perms.push("read");
                    perms.push("peek");
                }
            } else {
                const index = perms.indexOf(action);
                if (index > -1) {
                    perms.splice(index, 1);
                }
            }

            const newMap = new Map(prev).set(targetId, perms);
            return newMap;
        });
    };

    const processGroupPermissionRemovals = async (
        currentGroupIds: Set<string>,
        originalGroupIds: Set<string>,
        errors: string[],
    ): Promise<void> => {
        for (const groupId of originalGroupIds) {
            if (!currentGroupIds.has(groupId)) {
                const originalPerm = originalGroupPermissions.get(groupId);
                if (originalPerm) {
                    try {
                        await removeGroupPermission({ groupId, permissionId: originalPerm.permissionId }).unwrap();
                        await sleep(DELAY_BETWEEN_CALLS);
                    } catch (error) {
                        errors.push(`Failed to remove group ${groupId}: ${getErrorMessage(error)}`);
                    }
                }
            }
        }
    };

    const processGroupPermissionUpdates = async (errors: string[]): Promise<void> => {
        for (const group of allowedGroups) {
            const currentActions = groupPermissions.get(group.groupId) || [];
            const originalPerm = originalGroupPermissions.get(group.groupId);
            const originalActions = getOriginalActions(group.groupId, originalGroupPermissions);

            if (hasPermissionsChanged(currentActions, originalActions)) {
                // Remove old permission if it exists
                if (originalPerm) {
                    try {
                        await removeGroupPermission({
                            groupId: group.groupId,
                            permissionId: originalPerm.permissionId,
                        }).unwrap();
                        await sleep(DELAY_BETWEEN_CALLS);
                    } catch (error) {
                        errors.push(
                            `Failed to remove old permission for group ${group.name}: ${getErrorMessage(error)}`,
                        );
                        continue;
                    }
                }

                // Add new permission
                if (currentActions.length > 0) {
                    try {
                        await addGroupPermission({
                            groupId: group.groupId,
                            permission: {
                                type: "project",
                                projectId: props.project.projectId,
                                actions: currentActions,
                                subject: "projects",
                            },
                        }).unwrap();
                        await sleep(DELAY_BETWEEN_CALLS);
                    } catch (error) {
                        errors.push(`Failed to add permission for group ${group.name}: ${getErrorMessage(error)}`);
                    }
                }
            }
        }
    };

    const processUserPermissionRemovals = async (
        currentUsernames: Set<string>,
        originalUsernames: Set<string>,
        errors: string[],
    ): Promise<void> => {
        for (const username of originalUsernames) {
            if (!currentUsernames.has(username)) {
                const originalPerm = originalUserPermissions.get(username);
                if (originalPerm) {
                    try {
                        await removeUserPermission({ username, permissionId: originalPerm.permissionId }).unwrap();
                        await sleep(DELAY_BETWEEN_CALLS);
                    } catch (error) {
                        errors.push(`Failed to remove user ${username}: ${getErrorMessage(error)}`);
                    }
                }
            }
        }
    };

    const processUserPermissionUpdates = async (errors: string[]): Promise<void> => {
        for (const user of allowedUsers) {
            const currentActions = userPermissions.get(user.username) || [];
            const originalPerm = originalUserPermissions.get(user.username);
            const originalActions = getOriginalActions(user.username, originalUserPermissions);

            if (hasPermissionsChanged(currentActions, originalActions)) {
                // Remove old permission if it exists
                if (originalPerm) {
                    try {
                        await removeUserPermission({
                            username: user.username,
                            permissionId: originalPerm.permissionId,
                        }).unwrap();
                        await sleep(DELAY_BETWEEN_CALLS);
                    } catch (error) {
                        errors.push(
                            `Failed to remove old permission for user ${user.username}: ${getErrorMessage(error)}`,
                        );
                        continue;
                    }
                }

                // Add new permission
                if (currentActions.length > 0) {
                    try {
                        await addUserPermission({
                            username: user.username,
                            permission: {
                                type: "project",
                                projectId: props.project.projectId,
                                actions: currentActions,
                                subject: "projects",
                            },
                        }).unwrap();
                        await sleep(DELAY_BETWEEN_CALLS);
                    } catch (error) {
                        errors.push(`Failed to add permission for user ${user.username}: ${getErrorMessage(error)}`);
                    }
                }
            }
        }
    };

    const savePermissions = async () => {
        setIsSavingPermissions(true);
        const errors: string[] = [];

        try {
            // Process group permission changes
            const currentGroupIds = new Set(allowedGroups.map((g) => g.groupId));
            const originalGroupIds = new Set(originalGroupPermissions.keys());
            await processGroupPermissionRemovals(currentGroupIds, originalGroupIds, errors);
            await processGroupPermissionUpdates(errors);

            // Process user permission changes
            const currentUsernames = new Set(allowedUsers.map((u) => u.username));
            const originalUsernames = new Set(originalUserPermissions.keys());
            await processUserPermissionRemovals(currentUsernames, originalUsernames, errors);
            await processUserPermissionUpdates(errors);

            if (errors.length === 0) {
                Notifications.notify("Permissions updated", "Project permissions saved successfully.");
            } else {
                Notifications.notify("Partial success", `Some permissions could not be saved:\n${errors.join("\n")}`);
            }
        } catch (error) {
            Notifications.notify(
                "Error saving permissions",
                `Could not update project permissions: ${error instanceof Error ? error.message : "Unknown error"}`,
            );
        } finally {
            setIsSavingPermissions(false);
        }
    };

    const hasPermissionChanges = (): boolean => {
        const currentGroupIds = new Set(allowedGroups.map((g) => g.groupId));
        const originalGroupIds = new Set(originalGroupPermissions.keys());

        // Check if groups have been added or removed
        if (currentGroupIds.size !== originalGroupIds.size) {
            return true;
        }

        // Check if any group permissions have changed
        for (const group of allowedGroups) {
            const currentActions = groupPermissions.get(group.groupId) || [];
            const originalActions = getOriginalActions(group.groupId, originalGroupPermissions);
            if (hasPermissionsChanged(currentActions, originalActions)) {
                return true;
            }
        }

        const currentUsernames = new Set(allowedUsers.map((u) => u.username));
        const originalUsernames = new Set(originalUserPermissions.keys());

        // Check if users have been added or removed
        if (currentUsernames.size !== originalUsernames.size) {
            return true;
        }

        // Check if any user permissions have changed
        for (const user of allowedUsers) {
            const currentActions = userPermissions.get(user.username) || [];
            const originalActions = getOriginalActions(user.username, originalUserPermissions);
            if (hasPermissionsChanged(currentActions, originalActions)) {
                return true;
            }
        }

        return false;
    };

    return (
        <div>
            {props.project && (
                <>
                    {props.project.description != undefined && props.project.description?.trim() != "" && (
                        <div className="mb-3">
                            <h5>Description</h5>
                            <div>{props.project.description}</div>
                        </div>
                    )}

                    {props.project.mediaCount !== undefined && (
                        <div className="mb-3">
                            <h5>Video Count</h5>
                            <div>{props.project.mediaCount}</div>
                        </div>
                    )}

                    {props.project.createdAt && (
                        <div className="mb-3">
                            <h5>Created At</h5>
                            <div>{dayjs(props.project.createdAt).format(DateFormats.dayMonthYearWithTime)}</div>
                        </div>
                    )}

                    {props.project.createdBy && props.project.createdBy.type === "user" && (
                        <div className="mb-3">
                            <h5>Created By</h5>
                            <div>{props.project.createdBy.id}</div>
                        </div>
                    )}
                </>
            )}

            <div>
                <div className="group permissions-accordion">
                    <Accordion
                        activeKey={permissionsAccordionKeys}
                        onSelect={(eventKey: AccordionEventKey | null) => {
                            if (eventKey === null) {
                                setPermissionsAccordionKeys([]);
                            } else if (Array.isArray(eventKey)) {
                                setPermissionsAccordionKeys(
                                    eventKey.map((k) => k?.toString()).filter(Boolean) as string[],
                                );
                            } else {
                                setPermissionsAccordionKeys([eventKey.toString()]);
                            }
                        }}
                        alwaysOpen
                    >
                        <Accordion.Item eventKey="gperms">
                            <Accordion.Header>Group Permissions</Accordion.Header>
                            <Accordion.Body>
                                {userGroups && (
                                    <div>
                                        <div style={{ marginBottom: "1rem" }}>
                                            <Select
                                                isDisabled={isLoadingGroups || isLoadingPermissions}
                                                value={allowedGroups.map((group) => ({
                                                    value: group.groupId,
                                                    label: group.name,
                                                }))}
                                                options={userGroups.map((group) => ({
                                                    value: group.groupId,
                                                    label: group.name,
                                                }))}
                                                classNamePrefix="react-select"
                                                className="target-select"
                                                placeholder="Add group..."
                                                isMulti
                                                onChange={handleAllowedGroupsChange}
                                            />
                                        </div>

                                        {allowedGroups.length === 0 && !isLoadingPermissions && (
                                            <p
                                                className="text-muted"
                                                style={{ fontStyle: "italic", textAlign: "center" }}
                                            >
                                                <span>No groups have been granted access to this project.</span>
                                                <br />
                                                {allowedUsers.length > 0 && (
                                                    <span>Access has been granted to individual users.</span>
                                                )}
                                            </p>
                                        )}

                                        {allowedGroups.length > 0 && (
                                            <ConditionalSpinner size="sm" isLoading={isLoadingPermissions}>
                                                <PermissionsTable
                                                    items={allowedGroups}
                                                    itemKey={(item) => item.groupId}
                                                    itemName={(item) => item.name}
                                                    permissions={groupPermissions}
                                                    onPermissionChange={(input, id, action) =>
                                                        handlePermissionChange(input, id, action, setGroupPermissions)
                                                    }
                                                />
                                            </ConditionalSpinner>
                                        )}
                                    </div>
                                )}
                            </Accordion.Body>
                        </Accordion.Item>

                        <Accordion.Item eventKey="uperms">
                            <AccordionHeader>User Permissions</AccordionHeader>
                            <Accordion.Body>
                                {users && (
                                    <div>
                                        <div style={{ marginBottom: "1rem" }}>
                                            <Select
                                                isDisabled={
                                                    !userCanViewOtherUsers || isLoadingUsers || isLoadingPermissions
                                                }
                                                value={allowedUsers.map((user) => ({
                                                    value: user.username,
                                                    label: user.username,
                                                }))}
                                                options={users.map((user) => ({
                                                    value: user.username,
                                                    label: user.username,
                                                }))}
                                                classNamePrefix="react-select"
                                                placeholder="Add user..."
                                                isMulti
                                                onChange={handleAllowedUsersChange}
                                            />
                                        </div>

                                        {allowedUsers.length === 0 && !isLoadingPermissions && (
                                            <p
                                                className="text-muted"
                                                style={{ fontStyle: "italic", textAlign: "center" }}
                                            >
                                                No users have been individually granted access to this project. They may
                                                still have access through their group memberships.
                                            </p>
                                        )}
                                        {allowedUsers.length > 0 && (
                                            <ConditionalSpinner size="sm" isLoading={isLoadingPermissions}>
                                                <PermissionsTable
                                                    items={allowedUsers}
                                                    itemKey={(item) => item.username}
                                                    itemName={(item) => item.username}
                                                    permissions={userPermissions}
                                                    onPermissionChange={(input, id, action) =>
                                                        handlePermissionChange(input, id, action, setUserPermissions)
                                                    }
                                                />
                                            </ConditionalSpinner>
                                        )}
                                    </div>
                                )}
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </div>

                {hasPermissionChanges() && (
                    <SpinnerButton
                        className="mt-3"
                        isLoading={isSavingPermissions}
                        label="Save permissions"
                        buttonProps={{
                            type: "button",
                            variant: "success",
                            onClick: () => savePermissions(),
                            disabled: isLoadingPermissions || isLoadingGroups,
                        }}
                    />
                )}
            </div>
        </div>
    );
}
